import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Grid,
  Col,
  Row,
  Form,
  FormGroup,
  FormControl,
  ControlLabel,
  Button,
} from "react-bootstrap";
import Radio from "../../components/CustomRadio/CustomRadio";
import MandatoryLabel from "views/Components/MandatoryLabel";
import Select from "react-select";
import getLookups from "../Config/lookups.js";
import { constants } from "../../redux/providers/actions";
import { commonConstants } from "../../redux/common/actions";
import NotificationSystem from "react-notification-system";
import { style as notificationStyling } from "../../variables/Variables.jsx";
import ClipLoader from "react-spinners/ClipLoader";

const {
  UPDATE_PROVIDER_FORM_FIELD,
  RESET_PROVIDER_FORM_FIELDS,
  SUBMIT_PROVIDER_FORM,
  FETCH_PROVIDER,
  UPDATE_PROVIDER,
} = constants;
const { REQUEST } = commonConstants;
const Datetime = require("react-datetime");
const { providers, countries } = getLookups();
const states = require("../Config/usStates.json");
const provinces = require("../Config/canadaProvinces.json");
const australiaStates = require("../Config/australiaStates.json");

const ProviderForm = ({ match }) => {
  const { loading, error, success, list, ...form } = useSelector(
    (state) => state.providers
  );
  const dispatch = useDispatch();
  const notificationSystem = useRef();
  const [_notificationSystem, setNotificationSystem] = useState({});
  const searchParams = new URLSearchParams(window.location.search);
  const readOnlyMode =
    searchParams.has("readOnly") && searchParams.get("readOnly") === "true";

  const resetForm = (showMessage) =>
    dispatch({ type: RESET_PROVIDER_FORM_FIELDS, payload: { showMessage } });

  const handleSubmit = () =>
    dispatch({
      type: submitRoute + REQUEST,
      payload: formattedPayload,
    });

  const submitRoute =
    match.params.id > 0 ? UPDATE_PROVIDER : SUBMIT_PROVIDER_FORM;

  // initial page fetch
  useEffect(() => {
    if (match.params.id) {
      dispatch({
        type: FETCH_PROVIDER + REQUEST,
        payload: { id: match.params.id },
      });
    } else {
      resetForm(false);
    }
    setNotificationSystem(notificationSystem);
    return () => {};
  }, []);
  // handle ajax side effects
  useEffect(() => {
    const renderErrorMessages = (errors) =>
      errors.reduce((message, error) => {
        return `${message}${error}<br>`;
      }, "");
    const notificationSystem = _notificationSystem.current;
    if (notificationSystem && success.message) {
      notificationSystem.addNotification({
        message: success.message,
        level: "info",
        position: "tc",
      });
    }
    if (notificationSystem && error.message) {
      notificationSystem.addNotification({
        message: (
          <div
            dangerouslySetInnerHTML={{
              __html: renderErrorMessages(error.message),
            }}
          />
        ),
        level: "error",
        position: "tc",
      });
    }
    return () => {};
  }, [success.message, error.message]);
  // handle scroll to top for notifications
  useEffect(() => {
    // THIS IS SCREWY - Desktop works fine, Mobile scrolls to top quickly then back to original position - 2x fixed this
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    return () => {};
  }, [success.message, error.message]);

  const {
    name,
    coplineVerified,
    providerType,
    telehealth,
    dateVerified,
    email,
    website,
    address,
    city,
    state,
    country,
    zip,
    phone,
    hasSmsPhone,
    smsPhone,
    notes,
  } = form;
  const formattedPayload = form;

  const provinceStateLabelMap = { USA: "State/Territory", CANADA: "Province/Territory", AUSTRALIA: "State/Territory" };
  const provinceStateLabel = useMemo(() => {
    return provinceStateLabelMap[country?.value] || "State/Territory";
  }, [country.value]);
  const provinceStateCollectionMap = { USA: states, CANADA: provinces, AUSTRALIA: australiaStates };
  const provinceStateCollection = useMemo(() => {
    return provinceStateCollectionMap[country?.value] || states;
  }, [country.value]);
  const zipPostalCodeLabelMap = { USA: "Zip Code", CANADA: "Postal Code", AUSTRALIA: "Postcode" };
  const zipPostalCodeLabel = useMemo(() => {
    return zipPostalCodeLabelMap[country?.value] || "Zip Code";
  }, [country.value]);

  if (loading.source === FETCH_PROVIDER) {
    return (
      <div className="main-content" style={{ textAlign: "center" }}>
        <ClipLoader loading size={50} color="grey" />
      </div>
    );
  }

  return (
    <div className="main-content">
      <NotificationSystem
        ref={notificationSystem}
        style={notificationStyling}
      />
      <Form>
        <Grid fluid>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="Provider Type" />
                {readOnlyMode ? (
                  <FormControl
                    type="text"
                    id="providerTypeReadOnly"
                    name="providerTypeReadOnly"
                    disabled={true}
                    value={providerType.label}
                  />
                ) : (
                  <Select
                    options={providers.map((rank) => {
                      return { label: rank.label, value: rank.value };
                    })}
                    value={providerType}
                    isDisabled={readOnlyMode}
                    isSearchable={true}
                    name="providerType"
                    onChange={(event) =>
                      handleChange(dispatch, event, "providerType")
                    }
                  />
                )}
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="Telehealth" />
                <Radio
                  number="telehealth-true"
                  option={true}
                  name="telehealth"
                  onChange={(event) =>
                    handleChange(dispatch, event, "telehealth")
                  }
                  checked={telehealth}
                  disabled={readOnlyMode}
                  label="Yes"
                />
                <Radio
                  number="telehealth-false"
                  option={false}
                  name="telehealth"
                  onChange={(event) =>
                    handleChange(dispatch, event, "telehealth")
                  }
                  checked={!telehealth}
                  disabled={readOnlyMode}
                  label="No"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="Provider Name" />
                <FormControl
                  type="text"
                  id="name"
                  name="name"
                  value={name}
                  disabled={readOnlyMode}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Email</ControlLabel>
                <FormControl
                  type="text"
                  id="email"
                  name="email"
                  value={email}
                  disabled={readOnlyMode}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Telephone</ControlLabel>
                <FormControl
                  type="text"
                  id="phone"
                  name="phone"
                  value={phone}
                  disabled={readOnlyMode}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Website</ControlLabel>
                <FormControl
                  type="text"
                  id="website"
                  name="website"
                  value={website}
                  disabled={readOnlyMode}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
              <Col xs={3} sm={1}>
                <FormGroup>
                  <ControlLabel>Has SMS Phone</ControlLabel>
                  <Radio
                    number="hasSmsPhone-true"
                    option={true}
                    name="hasSmsPhone"
                    onChange={(event) => handleChange(dispatch, event)}
                    checked={hasSmsPhone}
                    label="Yes"
                    disabled={readOnlyMode}
                  />
                  <Radio
                    number="hasSmsPhone-false"
                    option={false}
                    name="hasSmsPhone"
                    onChange={(event) => handleChange(dispatch, event)}
                    checked={!hasSmsPhone}
                    label="No"
                    disabled={readOnlyMode}
                  />
                </FormGroup>
              </Col>
              {hasSmsPhone && (
                <Col xs={9} sm={5}>
                  <FormGroup>
                    <MandatoryLabel title="SMS Phone" />
                    <FormControl
                      type="text"
                      id="smsPhone"
                      name="smsPhone"
                      value={smsPhone}
                      disabled={readOnlyMode}
                      onChange={(event) => handleChange(dispatch, event)}
                    />
                  </FormGroup>
                </Col>
              )}
          </Row>
          <Row>
            <Col xs={12} sm={4}>
              <FormGroup>
                <MandatoryLabel title="Country" />
                {readOnlyMode && (
                  <FormControl
                    type="text"
                    id="countryReadOnly"
                    name="countryReadOnly"
                    disabled={true}
                    value={country.label}
                  />
                )}
                {!readOnlyMode && (
                  <Select
                    options={countries.map((country) => {
                      return { label: country.label, value: country.value };
                    })}
                    value={country}
                    isDisabled={readOnlyMode}
                    isSearchable={true}
                    name="country"
                    onChange={(event) =>
                      handleChange(dispatch, event, "country")
                    }
                  />
                )}
              </FormGroup>
            </Col>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>{provinceStateLabel}</ControlLabel>
                {readOnlyMode ? (
                  <FormControl
                    type="text"
                    id="stateReadOnly"
                    name="stateReadOnly"
                    disabled={true}
                    value={state.label}
                  />
                ) : (
                  <Select
                    options={provinceStateCollection.map((s) => {
                      return { label: s.label, value: s.value };
                    })}
                    value={state}
                    isDisabled={readOnlyMode}
                    isSearchable={true}
                    name="state"
                    onChange={(event) => handleChange(dispatch, event, "state")}
                  />
                )}
              </FormGroup>
            </Col>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>City</ControlLabel>
                <FormControl
                  type="text"
                  id="city"
                  name="city"
                  value={city}
                  disabled={readOnlyMode}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Address</ControlLabel>
                <FormControl
                  type="text"
                  id="address"
                  name="address"
                  value={address}
                  disabled={readOnlyMode}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>{zipPostalCodeLabel}</ControlLabel>
                <FormControl
                  type="text"
                  id="zip"
                  name="zip"
                  value={zip}
                  disabled={readOnlyMode}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={3} sm={2}>
              <FormGroup>
                <MandatoryLabel title="Copline Verified" />
                <Radio
                  number="coplineVerified-true"
                  option={true}
                  name="coplineVerified"
                  onChange={(event) =>
                    handleChange(dispatch, event, "coplineVerified")
                  }
                  checked={coplineVerified}
                  disabled={readOnlyMode}
                  label="Yes"
                />
                <Radio
                  number="coplineVerified-false"
                  option={false}
                  name="coplineVerified"
                  onChange={(event) =>
                    handleChange(dispatch, event, "coplineVerified")
                  }
                  checked={!coplineVerified}
                  disabled={readOnlyMode}
                  label="No"
                />
              </FormGroup>
            </Col>
            {coplineVerified && (
              <Col xs={9} sm={4}>
                <FormGroup>
                  <MandatoryLabel title="Date Verified" />
                  <Datetime
                    id="date-verified"
                    name="dateVerified"
                    timeFormat={false}
                    value={dateVerified ? new Date(dateVerified) : null}
                    inputProps={{ disabled: readOnlyMode }}
                    onChange={(event) =>
                      handleChange(dispatch, event, "dateVerified")
                    }
                  />
                </FormGroup>
              </Col>
            )}
          </Row>
          <Row>
            <Col xs={12} sm={12}>
              <FormGroup>
                <ControlLabel>Notes</ControlLabel>
                <FormControl
                  componentClass="textarea"
                  id="notes"
                  name="notes"
                  value={notes}
                  disabled={readOnlyMode}
                  style={{ height: "200px" }}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          {!readOnlyMode && (
            <Row style={{ marginTop: 20 }}>
              <Col xs={12} sm={12}>
                <Button
                  style={{ marginRight: "15px" }}
                  bsStyle="primary"
                  bsSize="lg"
                  onClick={handleSubmit}
                >
                  {match.params.id > 0 ? "Save" : "Submit"}
                </Button>
                <Button
                  bsStyle="warning"
                  type="reset"
                  bsSize="lg"
                  onClick={() => resetForm(true)}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          )}
          {readOnlyMode && (
            <Link to={`/admin/providers/`}>
              <Button bsStyle="warning" type="reset" bsSize="lg">
                Back
              </Button>
            </Link>
          )}
        </Grid>
      </Form>
    </div>
  );
};

function handleChange(dispatch, event, formField) {
  let field = null;
  let value = null;
  if (formField === "dateVerified") {
    field = formField;
    value = event._d;
  } else if (event.target) {
    field = event.target.name;
    value = event.target.value;
    value = value === "true" ? true : value;
    value = value === "false" ? false : value;
  } else {
    // select form items
    field = formField;
    value = event;
  }
  return dispatch({ type: UPDATE_PROVIDER_FORM_FIELD, field, value });
}

export default ProviderForm;
